import { debounce, shouldCloseOnFocusOut, throttle } from '../utils';

export default (livewireComponent = null, options = {}) => ({
  // Data properties
  itemSelected: livewireComponent,
  isMenuOpen: false,
  bgColor: 'bg-' + (options.highlightColor || 'brand-tertiary-300'),
  highlightColor: options.highlightColor || 'brand-tertiary-300',
  disabled: options.disabled || false,
  actAsButton: options.actAsButton || false,
  selectOptions: options.selectOptions || [],
  focusOnMount: options.focusOnMount || false,
  selectOptionsFiltered: options.selectOptions || [],
  menuWidthAuto: options.menuWidthAuto || false,
  displayLabel: '',
  displayLabelDefault: options.displayLabelDefault || '',
  focusedOptionIndex: -1,
  /** Guards the double init() — see the comment there. */
  isInitialised: false,
  itemHeight: options.itemHeight,
  renderCount: 20,
  startIdx: 0,
  scrollTop: 0,
  itemsElements: [],
  searchInput: '',
  noResults: false,
  name: options.name || '',
  isFixedPosition: false,
  searchable: options.searchable !== false,
  scrollableContainers: options.scrollableContainers || '',
  widthMenuFixed: false,

  get visibleOptions() {
    return this.selectOptionsFiltered.slice(this.startIdx, this.startIdx + this.renderCount);
  },
  get totalHeight() {
    return this.selectOptionsFiltered.length * this.itemHeight;
  },
  get offsetY() {
    return this.startIdx * this.itemHeight;
  },

  handleScroll(e) {
    this.scrollTop = e.target.scrollTop;
    // Clamp startIdx to prevent scrolling past the point where items can fill the view
    const maxStartIdx = Math.max(0, this.selectOptionsFiltered.length - this.renderCount);
    this.startIdx = Math.min(Math.floor(this.scrollTop / this.itemHeight), maxStartIdx);
  },

  // Utility methods
  getBackgroundColor() {
    let bgcolor = options.classString || '';
    const regex = /\bbg-[\w-]+/g;
    const match = bgcolor.match(regex);
    return match ? match[0] : '';
  },

  focusInput() {
    const input = this.$refs.input;
    if (input) input.focus();
  },

  scrollToFocused() {
    if (this.focusedOptionIndex === -1) return;
    const itemTop = this.focusedOptionIndex * this.itemHeight;
    const itemBottom = itemTop + this.itemHeight;
    const menu = this.$refs.menu;
    if (!menu) return;
    const menuHeight = menu.clientHeight;
    
    if (itemTop < this.scrollTop) {
      menu.scrollTop = itemTop;
      this.scrollTop = itemTop;
    } else if (itemBottom > this.scrollTop + menuHeight) {
      menu.scrollTop = itemBottom - menuHeight;
      this.scrollTop = menu.scrollTop;
    }
    this.startIdx = Math.floor(this.scrollTop / this.itemHeight);
  },

  activeSelected() {
    const idx = this.selectOptionsFiltered.findIndex(o => o.value === this.itemSelected);
    if (idx !== -1) {
      this.focusedOptionIndex = idx;
    } else if (this.selectOptionsFiltered.length > 0) {
      this.focusedOptionIndex = 0;
    } else {
      this.focusedOptionIndex = -1;
    }
    this.$nextTick(() => this.scrollToFocused());
  },

  maybeFixPosition() {
    if (!this.scrollableContainers) return;
    const popupContainer = this.$refs.popupContainer;
    const sortBtn = this.$refs.sortBtn;
    const sortBtnRect = sortBtn.getBoundingClientRect();
    const popupContainerRect = popupContainer.getBoundingClientRect();

    const viewportWidth = window.innerWidth - 16;
    const viewportHeight = window.innerHeight - 16;

    popupContainer.style.visibility = 'hidden';
    popupContainer.style.display = 'block';
    popupContainer.style.position = 'fixed';
    this.isFixedPosition = true;

    // When the menu opens UPWARD its bottom edge is pinned to the trigger, so a list that
    // shrinks as the search filters it would crawl away from the control. Pinning a fixed
    // height stops that.
    //
    // The class comes from the menu's own data attribute rather than a hard-coded 'h-60':
    // `height` and `max-height` are different properties, so a literal h-60 (height:15rem)
    // silently overrode any caller that had raised menuMaxHeightClass — the chart-type
    // picker asks for 26rem so all fourteen types fit, and got a cramped 15rem list instead
    // whenever it happened to open upward. Callers that never set it still get h-60.
    const fixedHeightClass = this.$refs.menu.dataset.fixedHeightClass || 'h-60';

    // Cleared before the natural height is read, so the flip decision is made from the
    // content's own height rather than from whatever height the previous open pinned.
    this.$refs.menu.classList.remove(fixedHeightClass);

    let popupContainerHeight = popupContainer.offsetHeight;
    popupContainer.style.width = '1px'; // That way the menu would only take up width as needed

    let top = sortBtnRect.top + sortBtnRect.height;
    let left = sortBtnRect.left;

    if (top + popupContainerHeight > viewportHeight) {
      // Pin the height BEFORE deriving the upward position, then MEASURE AGAIN. An upward
      // menu is placed by subtracting its own height, and the pin routinely makes the popup
      // taller than it just measured — six options are ~272px of rows, while h-[26rem] forces
      // 416px. Positioning from the pre-pin height therefore drops the popup by the whole
      // difference, so its bottom edge lands below the trigger and blankets the control that
      // opened it (~200px on the chart-type picker, ~60px on the interval select).
      //
      // This has to stay in this order. Pinning after the fact was survivable only by
      // accident before the class was ever removed: the first upward open mispositioned, and
      // every later one happened to measure the still-pinned height and came out right.
      this.$refs.menu.classList.add(fixedHeightClass);
      popupContainerHeight = popupContainer.offsetHeight;

      const flippedTop = sortBtnRect.top - popupContainerHeight;

      if (flippedTop >= 0) {
        top = flippedTop;
      } else {
        // Neither direction fits. Flipping anyway would put the popup's top above the
        // viewport, and the Math.max(0, ...) clamp below would then slide it DOWN over the
        // control that opened it — the taller the menu the worse it gets, which is why a
        // raised menuFixedHeightClass (h-[26rem] = 416px) makes it likeliest on a short
        // window or at 400% zoom. Staying below the trigger and dropping the pin lets the
        // list shrink into whatever room exists: it may run past the bottom edge, which the
        // page can scroll to, but it never covers the trigger.
        this.$refs.menu.classList.remove(fixedHeightClass);
      }
    }

    // Prevent off-screen (right edge)
    if (left + popupContainer.offsetWidth > viewportWidth) {
      left = viewportWidth - popupContainer.offsetWidth;
    }

    popupContainer.style.top = `${Math.max(0, top + 5)}px`;
    popupContainer.style.left = `${Math.max(0, left)}px`;

    popupContainer.style.removeProperty('visibility');
    popupContainer.style.removeProperty('display');
  },

  onMenuOpen() {
    this.maybeFixPosition()
    this.attachScrollEventListeners()
    
    if (!this.searchable) this.searchInput = '';

    setTimeout(() => {
      if (!this.isMenuOpen) return;
      this.focusInput();
      this.activeSelected();
    }, 50);

    setTimeout(() => {
      if (!this.isMenuOpen) return;
      this.fixMenuWidth();
    }, 120);
  },

  attachScrollEventListeners() {
    if (!this.scrollableContainers) return;
    const onScrollAdjustPosition = throttle((e) => {
      if (!this.isMenuOpen) {
        e.target.removeEventListener('scroll', onScrollAdjustPosition)
        return;
      }

      if (!this.isElementInvisibleInContainer(this.$refs.sortBtn, e.target)) {
        this.closeAndRestoreFocus()
        e.target.removeEventListener('scroll', onScrollAdjustPosition)
        return;
      }

      this.maybeFixPosition()
    }, 10)

    const elements = document.querySelectorAll(this.scrollableContainers);
    elements.forEach(el => {
      el.addEventListener('scroll', onScrollAdjustPosition)
    });
  },

  isElementInvisibleInContainer(el, container) {
    const elRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    return (
      elRect.top >= containerRect.top &&
      elRect.bottom <= containerRect.bottom &&
      elRect.left >= containerRect.left &&
      elRect.right <= containerRect.right
    );
  },

  handleKeydown() {
    if (this.isMenuOpen) return;
    this.isMenuOpen = true;
    this.$nextTick(() => {
      this.onMenuOpen();
    });
  },

  handleFocusOut(event) {
    if (!this.isMenuOpen) return;
    // $refs.sortBtn IS the element a <label for> resolves to, so its live id is the one to match.
    if (shouldCloseOnFocusOut(event, {
      triggerId: this.$refs.sortBtn?.id || options.id,
      componentEl: this.$el,
      menuEl: this.$refs.popupContainer,
    })) {
      this.isMenuOpen = false;
    }
  },

  handleBtnClick(e) {
    if (this.isMenuOpen) {
      this.closeAndRestoreFocus();
    } else {
      this.isMenuOpen = true;
      this.$nextTick(() => {
        this.onMenuOpen();
      });
    }
  },

  fixMenuWidth() {
    if (this.widthMenuFixed) return;
    let popupContainer = this.$refs.popupContainer;
    if (!popupContainer) return;
    const width = popupContainer.getBoundingClientRect().width;
    if (width === 0) return;
    popupContainer.style.width = width + 'px';
    popupContainer.style.minWidth = width + 'px';
    popupContainer.style.maxWidth = width + 'px';
    this.widthMenuFixed = true;
  },

  handleEscapeKeyPress() {
    if (!this.isMenuOpen) return;

    // Escape clears a search before it closes the menu — but only when the search box is
    // actually VISIBLE. On an unsearchable select the input is sr-only yet still focused, so
    // a stray printable key silently filters a list with no search box on screen to explain
    // it, and the old unconditional early-return then left Escape doing nothing at all: no
    // close, no visible cause, no way out except clicking elsewhere.
    if (this.searchable && this.searchInput) {
      this.searchInput = '';
      return;
    }

    this.closeAndRestoreFocus();
  },

  closeAndRestoreFocus() {
    this.isMenuOpen = false;
    this.restoreFocus();
    this.widthMenuFixed = false;
    if (this.$refs.popupContainer) {
      this.$refs.popupContainer.style.width = '';
      this.$refs.popupContainer.style.minWidth = '';
    }
  },

  restoreFocus() {
    this.$nextTick(() => {
      this.$refs.sortBtn.focus();
    });
  },

  moveActiveDown() {
    if (this.noResults) return;
    if (this.focusedOptionIndex >= this.selectOptionsFiltered.length - 1) {
      this.focusedOptionIndex = 0;
    } else {
      this.focusedOptionIndex++;
    }
    this.scrollToFocused();
  },

  moveActiveUp() {
    if (this.noResults) return;
    if (this.focusedOptionIndex <= 0) {
      this.focusedOptionIndex = this.selectOptionsFiltered.length - 1;
    } else {
      this.focusedOptionIndex--;
    }
    this.scrollToFocused();
  },

  handleKeyEnter() {
    if (this.noResults || this.focusedOptionIndex === -1) return;
    const option = this.selectOptionsFiltered[this.focusedOptionIndex];
    if (option) {
      this.handleItemClicked(option.value, option.label);
    }
  },

  handleItemClicked(value, label) {
    if (this.itemSelected !== value) {
      this.itemSelected = value;
      if (!this.actAsButton) {
        this.displayLabel = label;
      }
      if (options.returnArray) {
        let name = this.name ? this.name : '';
        this.$dispatch('item_selected_select2', { selected: this.itemSelected, name: name, label });
      } else {
        this.$dispatch(`${options.id}_item_selected_select2`, { selected: this.itemSelected, label });
      }
    }
    this.closeAndRestoreFocus();
  },

  // Initialize watchers and setup
  init() {
    // Idempotent, because init() runs TWICE per mount: Alpine auto-invokes an x-data
    // object's init(), and the blade also carries x-init="init()". Both $watch()es were
    // therefore registered twice (two full selectOptions.filter() passes per keystroke),
    // and — worse — handleUpdateOptions/handleSetSelected are reassigned between the two
    // runs while destroy() removes only the last pair, leaking one window listener per
    // mount that holds the torn-down component scope. Guarding here rather than deleting
    // the x-init: this component has ~112 call sites, and the guard is correct however it
    // is invoked.
    if (this.isInitialised) return;
    this.isInitialised = true;

    // Watch itemSelected changes
    this.$watch('itemSelected', (newValue, oldValue) => {
      if (newValue != oldValue && !this.actAsButton) {
        this.displayLabel = this.selectOptions.find(o => o.value === newValue)?.label;
      }
    });

    // Watch searchInput changes
    this.$watch('searchInput', debounce(value => {
      const searchTerm = value.toLowerCase().split('').join('.*');
      const searchRegex = new RegExp(searchTerm);
      this.selectOptionsFiltered = this.selectOptions.filter(item => {
        const label = item.label || '';
        const info = item.info || '';
        return searchRegex.test(label.toLowerCase()) || searchRegex.test(info.toLowerCase());
      });
      this.noResults = this.selectOptionsFiltered.length < 1;
      this.scrollTop = 0;
      this.startIdx = 0;
      if (this.$refs.menu) this.$refs.menu.scrollTop = 0;
      setTimeout(() => {
        this.activeSelected();
      }, 50);
    }, 100));
    
    this.handleUpdateOptions = (event) => {
      if (event.detail && event.detail.id === options.id && event.detail.options?.length) {
        this.selectOptions = event.detail.options;
        this.selectOptionsFiltered = event.detail.options;

        if (this.$refs.sortBtn) {
            this.$refs.sortBtn.removeAttribute('disabled')
        }
        this.disabled = false;
        
        // Update display label if current selection still exists
        const currentOption = this.selectOptions.find(o => o.value === this.itemSelected);
        if (currentOption && !this.actAsButton) {
          this.displayLabel = currentOption.label;
        } else if (!currentOption && this.selectOptions.length > 0 && !this.actAsButton) {
          // If current selection doesn't exist anymore, reset to first option
          this.displayLabel = this.selectOptions[0].label;
        }
      }
    };

    // Listen for external updates to selectOptions
    // Event name: {id}_update-select2-options
    window.addEventListener(`${options.id}_update-select2-options`, this.handleUpdateOptions);

    this.handleSetSelected = (event) => {
        if (event.detail && typeof event.detail.value !== 'undefined') {
            const found = this.selectOptions.find(o => o.value === event.detail.value);
            if (found) {
                this.itemSelected = found.value;
                if (!this.actAsButton) this.displayLabel = found.label;
            }
        }
    };
    window.addEventListener(`${options.id}_set-selected-select2`, this.handleSetSelected);

    // Set initial display label
    if (this.displayLabelDefault) {
      this.displayLabel = this.displayLabelDefault
    } else {
      this.displayLabel = this.selectOptions.find(o => o.value === this.itemSelected)?.label;
      if (!this.displayLabel && this.selectOptions.length > 0) {
        this.displayLabel = this.selectOptions[0].label;
      }
    }

    // Check for duplicate values
    const values = this.selectOptions.map(option => option.value);
    const hasDuplicates = values.length !== new Set(values).size;
    if (hasDuplicates) {
      throw new Error('Duplicate values found in x-select2!');
    }

    // Focus on mount if needed
    if (this.focusOnMount) {
      this.restoreFocus();
    }
  },

  destroy() {
    if (this.handleUpdateOptions) {
        window.removeEventListener(`${options.id}_update-select2-options`, this.handleUpdateOptions);
    }
    if (this.handleSetSelected) {
        window.removeEventListener(`${options.id}_set-selected-select2`, this.handleSetSelected);
    }
  }
})